import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Liquor Tracking: restaurant-scoped sets plus per-location stocktakes; report-only, never changing stock.
const restBase = (restaurantId) => `${API_URL}/users/restaurant/${restaurantId}`;
const locBase = (restaurantId, locationId) =>
  `${API_URL}/users/restaurant/${restaurantId}/locations/${locationId}`;

const qs = (params = {}) => {
  const s = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ).toString();
  return s ? `?${s}` : '';
};

// ---- Liquor sets ----------------------------------------------------------

export const listSets = async (restaurantId) => {
  const { data } = await axios.get(`${restBase(restaurantId)}/liquor-sets/`);
  return data?.results || [];
};

export const getSet = async (restaurantId, setId) => {
  const { data } = await axios.get(`${restBase(restaurantId)}/liquor-sets/${setId}/`);
  return data?.data || null;
};

// payload: { name, description, price_basis, dish_ids: [] }
export const createSet = async (restaurantId, payload) => {
  const { data } = await axios.post(`${restBase(restaurantId)}/liquor-sets/`, payload);
  return data?.data;
};

export const updateSet = async (restaurantId, setId, payload) => {
  const { data } = await axios.put(`${restBase(restaurantId)}/liquor-sets/${setId}/`, payload);
  return data?.data;
};

export const deleteSet = async (restaurantId, setId) => {
  const { data } = await axios.delete(`${restBase(restaurantId)}/liquor-sets/${setId}/`);
  return data?.success;
};

// Selectable liquors for hand-picking a set, each with cost and selling price.
export const listCandidates = async (restaurantId, params = {}) => {
  const { data } = await axios.get(`${restBase(restaurantId)}/liquor-candidates/${qs(params)}`);
  return data?.results || [];
};

// ---- Stocktake sessions ---------------------------------------------------

export const listStocktakes = async (restaurantId, locationId) => {
  const { data } = await axios.get(`${locBase(restaurantId, locationId)}/liquor-stocktakes/`);
  return data?.results || [];
};

// payload: { liquor_set_id, frequency, name, note }
export const createStocktake = async (restaurantId, locationId, payload) => {
  const { data } = await axios.post(`${locBase(restaurantId, locationId)}/liquor-stocktakes/`, payload);
  return data?.data;
};

// The workspace: { stocktake, expected, scanned, remaining }
export const getStocktake = async (restaurantId, locationId, stocktakeId) => {
  const { data } = await axios.get(`${locBase(restaurantId, locationId)}/liquor-stocktakes/${stocktakeId}/`);
  return data || null;
};

// Record or overwrite one counted line.
export const scanCount = async (restaurantId, locationId, stocktakeId, payload) => {
  const { data } = await axios.post(
    `${locBase(restaurantId, locationId)}/liquor-stocktakes/${stocktakeId}/scan/`, payload,
  );
  return data?.data;
};

export const unscanCount = async (restaurantId, locationId, stocktakeId, dishId) => {
  const { data } = await axios.delete(
    `${locBase(restaurantId, locationId)}/liquor-stocktakes/${stocktakeId}/scan/${dishId}/`,
  );
  return data?.success;
};

// Resolve a scanned barcode to an expected liquor in this session.
export const lookupBarcode = async (restaurantId, locationId, stocktakeId, code) => {
  const { data } = await axios.get(
    `${locBase(restaurantId, locationId)}/liquor-stocktakes/${stocktakeId}/barcode-lookup/${qs({ code })}`,
  );
  return data; // { success, found, data?, error? }
};

export const completeStocktake = async (restaurantId, locationId, stocktakeId) => {
  const { data } = await axios.post(
    `${locBase(restaurantId, locationId)}/liquor-stocktakes/${stocktakeId}/complete/`, {},
  );
  return data?.data;
};

// Variance report: { stocktake, totals, lines, unscanned }
export const getReport = async (restaurantId, locationId, stocktakeId) => {
  const { data } = await axios.get(
    `${locBase(restaurantId, locationId)}/liquor-stocktakes/${stocktakeId}/report/`,
  );
  return data || null;
};
